/**
 * Exercise data utilities for programmatic SEO pages.
 *
 * This file contains ONLY client-safe code (types, labels, helpers).
 * Server-side data loading lives in exercises.server.ts.
 *
 */

import { isNumber } from 'fitify-types/src/type-guards/common'
import type {
  ExerciseCategory,
  ExerciseInstructions,
  ExerciseMuscleIntensity,
  FitifyExerciseData,
} from 'fitify-types/src/types/exercise-collection'
import { DateTime } from 'luxon'

export type { ExerciseCategory, ExerciseMuscleIntensity, ExerciseInstructions }

/** Exercise data enriched with computed fields for the web. */
export type EnrichedExerciseData = FitifyExerciseData & {
  slug: string
}

export type ExerciseItem = Pick<
  EnrichedExerciseData,
  | 'code'
  | 'impact'
  | 'instructions'
  | 'muscle_intensity'
  | 'muscle_intensity_stretch'
  | 'noisy'
  | 'sexyness_f'
  | 'sexyness_m'
  | 'slug'
  | 'skill_required'
  | 'stance'
  | 'thumbnail'
  | 'title'
  | 'tool'
  | 'video'
>

export type ExerciseListItem = Pick<
  EnrichedExerciseData,
  'code' | 'muscle_intensity' | 'slug' | 'thumbnail' | 'title' | 'tool'
>

export type Muscle = {
  key: string
  intensity: number
}

export const transformEnrichedExerciseToExerciseListItem = (
  exercise: EnrichedExerciseData
): ExerciseListItem => {
  return {
    code: exercise.code,
    ...('muscle_intensity' in exercise
      ? { muscle_intensity: exercise.muscle_intensity }
      : {}),
    slug: exercise.slug,
    ...('thumbnail' in exercise ? { thumbnail: exercise.thumbnail } : {}),
    title: exercise.title,
    tool: exercise.tool,
  }
}

export const transformEnrichedExerciseToExerciseItem = (
  exercise: EnrichedExerciseData
): ExerciseItem => {
  return {
    ...transformEnrichedExerciseToExerciseListItem(exercise),
    impact: exercise.impact,
    ...('instructions' in exercise
      ? { instructions: exercise.instructions }
      : {}),
    ...('muscle_intensity_stretch' in exercise
      ? { muscle_intensity_stretch: exercise.muscle_intensity_stretch }
      : {}),
    noisy: exercise.noisy,
    sexyness_f: exercise.sexyness_f,
    sexyness_m: exercise.sexyness_m,
    skill_required: exercise.skill_required,
    stance: exercise.stance,
    ...('video' in exercise ? { video: exercise.video } : {}),
  }
}

// ---------------------------------------------------------------------------
// Slug generation
// ---------------------------------------------------------------------------

export const generateSlug = (title: string): string => {
  return title
    .toLowerCase()
    .replace(/['\u2018\u2019]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

// ---------------------------------------------------------------------------
// i18n key helpers — return translation keys for use with t()
// ---------------------------------------------------------------------------

export const getDifficultyI18nKey = (skillRequired: number): string => {
  if (skillRequired <= 3) return 'landing_exercises_difficulty_beginner'
  if (skillRequired <= 6) return 'landing_exercises_difficulty_intermediate'
  return 'landing_exercises_difficulty_advanced'
}

export const getImpactI18nKey = (impact: number): string => {
  if (impact === 0) return 'landing_exercises_impact_low'
  if (impact === 1) return 'landing_exercises_impact_medium'
  return 'landing_exercises_impact_high'
}

export const getStanceI18nKey = (stance: string): string => {
  switch (stance) {
    case 'S':
      return 'landing_exercises_stance_standing'
    case 'K':
      return 'landing_exercises_stance_kneeling'
    case 'F':
      return 'landing_exercises_stance_floor'
    default:
      return 'landing_exercises_stance_standing'
  }
}

export const getToolI18nKey = (tool: string | null): string => {
  return `landing_exercises_tool_${tool || 'bodyweight'}`
}

const MUSCLE_I18N_KEY_OVERRIDES: Record<string, string> = {
  hams: 'hamstrings',
  shoulders_front: 'shoulders',
}

export const getMuscleI18nKey = (muscleKey: string): string => {
  const mapped = MUSCLE_I18N_KEY_OVERRIDES[muscleKey] || muscleKey
  return `landing_exercises_muscle_${mapped}`
}

// ---------------------------------------------------------------------------
// Muscle extraction
// ---------------------------------------------------------------------------

const extractMuscles = (
  intensity: ExerciseMuscleIntensity | undefined,
  minIntensity: number,
  maxIntensity: number
): Muscle[] => {
  if (!intensity) return []
  return Object.entries(intensity)
    .filter(
      ([, value]) =>
        isNumber(value) && value >= minIntensity && value <= maxIntensity
    )
    .sort(([, aValue], [, bValue]) => bValue - aValue)
    .map(([key, value]) => ({ key, intensity: value }))
}

/** Primary muscles: intensity 5+ (strongly engaged). */
export const getPrimaryMuscles = (
  intensity: ExerciseMuscleIntensity | undefined
): Muscle[] => {
  return extractMuscles(intensity, 5, Infinity)
}

export const getMusclesWorked = (
  intensity: ExerciseMuscleIntensity | undefined
): Muscle[] => {
  return extractMuscles(intensity, 1, Infinity)
}

// ---------------------------------------------------------------------------
// Enrichment (used by server-side loader, exported for reuse)
// ---------------------------------------------------------------------------

export const enrichExercise = (
  exercise: FitifyExerciseData
): EnrichedExerciseData => {
  return {
    ...exercise,
    slug: generateSlug(exercise.title),
  }
}

// ---------------------------------------------------------------------------
// Related exercises scoring
// ---------------------------------------------------------------------------

const computeMuscleOverlap = (
  a: ExerciseMuscleIntensity | undefined,
  b: ExerciseMuscleIntensity | undefined
): number => {
  if (!a || !b) {
    return 0
  }

  let overlap = 0

  for (const key of Object.keys(a) as (keyof ExerciseMuscleIntensity)[]) {
    const aValue = a[key] || 0
    const bValue = b[key] || 0
    if (aValue > 0 && bValue > 0) {
      overlap += Math.min(aValue, bValue)
    }
  }

  return overlap
}

export const scoreRelatedExercises = (
  exercise: ExerciseItem,
  candidates: EnrichedExerciseData[],
  count: number
): EnrichedExerciseData[] => {
  const scored = candidates
    .filter((exerciseCandidate) => exerciseCandidate.code !== exercise.code)
    .map((exerciseCandidate) => {
      let score = 0
      if (exerciseCandidate.tool === exercise.tool) {
        score += 10
      }
      score += computeMuscleOverlap(
        exerciseCandidate.muscle_intensity,
        exercise.muscle_intensity
      )
      return { exercise: exerciseCandidate, score }
    })
    .sort((a, b) => b.score - a.score)

  return scored.slice(0, count).map(({ exercise }) => exercise)
}

// ---------------------------------------------------------------------------
// Popularity calculation
// ---------------------------------------------------------------------------

/**
 * Estimated total daily exercise completions across all Fitify users.
 * Used to generate realistic-looking popularity numbers for the SEO pages.
 * Formula: (TOTAL_DAILY * toolShare * sexyness^1.5 * nameModifier) / (toolCount * 6^1.5)
 */
const TOTAL_DAILY = 1_830_000

/** Percentage of daily exercises tracked per tool. */
const TOOL_DISTRIBUTION: Record<string, number> = {
  bodyweight: 0.852,
  yoga: 0.0551,
  dumbbell: 0.0382,
  kettlebell: 0.0202,
  trx: 0.008,
  foamroller: 0.0059,
  swissball: 0.0054,
  barbell: 0.005,
  resistanceband: 0.0041,
  medicineball: 0.0027,
  bosu: 0.0023,
  pullupbar: 0.0015,
  gym_and_other: 0.001,
}

/** Number of exercises per tool (for dividing the pool). */
const EXERCISES_PER_TOOL: Record<string, number> = {
  bodyweight: 268,
  yoga: 127,
  dumbbell: 104,
  kettlebell: 61,
  trx: 98,
  foamroller: 58,
  swissball: 53,
  barbell: 34,
  medicineball: 61,
  resistanceband: 31,
  pullupbar: 5,
  bosu: 67,
  gym_and_other: 291,
}

/**
 * Deterministic daily modifier in range [0.8, 1.2] based on date.
 * The exerciseSeed shifts the hash so each exercise gets a different
 * curve shape across the 7 days.
 */
const getDailyModifier = (
  { day, month, year }: DateTime,
  exerciseSeed: number
): number => {
  const hash = ((day * 7 + month * 13 + year * 3 + exerciseSeed * 17) % 41) / 41
  return 0.8 + hash * 0.4
}

/**
 * Per-exercise static modifier (±15%) based on name characters.
 * Ensures exercises with identical tool + sexyness still get different numbers.
 */
const getExerciseNameModifier = (code: string): number => {
  const first = code.charCodeAt(0) || 0
  const last = code.charCodeAt(code.length - 1) || 0
  const hash = ((first * 31 + last * 17) % 61) / 61 // 0..1
  return 0.85 + hash * 0.3 // 0.85..1.15
}

export type ExercisePopularity = {
  count: number
  last8w: number[]
  isTrendUp: boolean
}

export const getExercisePopularity = (
  exercise: ExerciseItem
): ExercisePopularity => {
  const tool = exercise.tool || 'bodyweight'
  const toolShare = TOOL_DISTRIBUTION[tool] ?? 0.001
  const toolCount = EXERCISES_PER_TOOL[tool] ?? 100

  // Sexyness coefficient: higher = more popular, weighted with power curve
  const sexyness = Math.max(exercise.sexyness_m ?? 0, exercise.sexyness_f ?? 0)
  const coeff = Math.pow(Math.max(sexyness, 1), 1.5)

  // Average coefficient for the tool group (approximation using distribution median ~6)
  const avgCoeff = Math.pow(6, 1.5)

  // Base count with per-exercise name modifier for variety
  const nameModifier = getExerciseNameModifier(exercise.code)
  const baseCount =
    (TOTAL_DAILY * toolShare * coeff * nameModifier) / (toolCount * avgCoeff)

  // Seed for weekly variation — different per exercise so curves differ
  const exerciseSeed =
    exercise.code.charCodeAt(0) +
    exercise.code.charCodeAt(Math.floor(exercise.code.length / 2)) +
    exercise.code.length

  // Weekly totals for the last 8 weeks (each week = baseCount * 7 * modifier)
  const last8w: number[] = []

  for (let week = 7; week >= 0; week--) {
    const dateTime = DateTime.local().minus({ week })
    const modifier = getDailyModifier(dateTime, exerciseSeed)
    last8w.push(Math.round(baseCount * 7 * modifier))
  }

  const count = last8w[7] // this week
  const isTrendUp = last8w[7] >= last8w[6]

  return { count, last8w, isTrendUp }
}
